import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  User as UserIcon, 
  Settings, 
  FileText, 
  Lock, 
  LogOut, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  AlertTriangle,
  Info,
  ShieldCheck,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  Activity,
  Sparkles
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { auth } from '../lib/firebase';
import { signOut, onAuthStateChanged, User as FirebaseUser, updateProfile } from 'firebase/auth';
import { dbService, AuditRecord, VettingProfile, Invariant } from '../services/dbService';
import { geminiService } from '../services/geminiService';
import Markdown from 'react-markdown';

export default function MemberDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<FirebaseUser | null>(auth.currentUser);
  const [audits, setAudits] = useState<AuditRecord[]>([]);
  const [vettingProfiles, setVettingProfiles] = useState<VettingProfile[]>([]);
  const [invariants, setInvariants] = useState<Invariant[]>([]);
  const [profileData, setProfileData] = useState<{ displayName?: string, photoURL?: string, diagnosticReport?: string, lastReportDate?: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Vetting Modal State
  const [isVettingModalOpen, setIsVettingModalOpen] = useState(false);
  const [newVetting, setNewVetting] = useState({
    name: '',
    stage: 'Observation' as VettingProfile['stage'],
    riskLevel: 'Medium' as VettingProfile['riskLevel'],
    notes: ''
  });

  // Profile Edit State
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    photoURL: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const [allAuditsData, vettingData, invariantData, profile] = await Promise.all([
            dbService.getAllAudits(currentUser.uid),
            dbService.getVettingProfiles(currentUser.uid),
            dbService.getInvariants(currentUser.uid),
            dbService.getUserProfile(currentUser.uid)
          ]);
          setAudits(allAuditsData);
          setVettingProfiles(vettingData);
          setInvariants(invariantData);
          setProfileData(profile as any);
        } catch (err) {
          console.error("Dashboard data load failed:", err);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAddVetting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      await dbService.addVettingProfile(user.uid, newVetting);
      const updated = await dbService.getVettingProfiles(user.uid);
      setVettingProfiles(updated);
      setIsVettingModalOpen(false);
      setNewVetting({ name: '', stage: 'Observation', riskLevel: 'Medium', notes: '' });
    } catch (err) {
      console.error("Vetting expansion failed:", err);
    }
  };

  const handleDeleteVetting = async (id: string) => {
    if (!user || !confirm("Terminate this dossier?")) return;
    try {
      await dbService.deleteVettingProfile(user.uid, id);
      setVettingProfiles(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Dossier termination failed:", err);
    }
  };

  const toggleInvariant = async (inv: Invariant) => {
    if (!user) return;
    try {
      await dbService.toggleInvariant(user.uid, inv.id!, !inv.isActive);
      setInvariants(prev => prev.map(p => p.id === inv.id ? { ...p, isActive: !p.isActive } : p));
    } catch (err) {
      console.error("Boundary sync failed:", err);
    }
  };

  useEffect(() => {
    if (audits.length > 0 && profileData && !profileData.diagnosticReport && !isGenerating) {
      const timer = setTimeout(() => {
        generateDiagnosticSynthesis();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [audits.length, profileData, isGenerating]);

  const generateDiagnosticSynthesis = async () => {
    if (!user || audits.length === 0) return;
    setIsGenerating(true);
    try {
      const report = await geminiService.generateDiagnosticReport(audits);
      await dbService.updateUserProfile(user.uid, {
        diagnosticReport: report,
        lastReportDate: new Date() as any
      });
      setProfileData(prev => prev ? { ...prev, diagnosticReport: report, lastReportDate: new Date() } : null);
    } catch (err) {
      console.error("Report generation failed:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfileMessage(null);

    if (file.size > 1024 * 500) { // 500KB limit for base64 storage demo
      setProfileMessage({ 
        type: 'error', 
        text: "The archive prohibits files exceeding 500KB for integrity." 
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileForm(prev => ({ ...prev, photoURL: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setSavingProfile(true);
    setProfileMessage(null);
    try {
      await dbService.updateUserProfile(user.uid, {
        displayName: profileForm.displayName,
        photoURL: profileForm.photoURL
      });

      try {
        await updateProfile(user, {
          displayName: profileForm.displayName
        });
      } catch (authErr) {
        console.warn("Auth profile update (non-critical) failed:", authErr);
      }

      setProfileData(prev => prev ? {
        ...prev,
        displayName: profileForm.displayName,
        photoURL: profileForm.photoURL
      } : {
        displayName: profileForm.displayName,
        photoURL: profileForm.photoURL
      });
      
      const updatedUser = Object.assign(Object.create(Object.getPrototypeOf(auth.currentUser!)), auth.currentUser!);
      setUser(updatedUser);
      
      setProfileMessage({ type: 'success', text: "Profile recalibrated and archived successfully." });
      
      setTimeout(() => {
        setIsProfileModalOpen(false);
        setProfileMessage(null);
      }, 1500);
    } catch (err: any) {
      console.error("Profile update failed:", err);
      setProfileMessage({ 
        type: 'error', 
        text: `Resource update failed: ${err.message || 'Unknown protocol error'}` 
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const openProfileEdit = () => {
    setProfileForm({
      displayName: user?.displayName || '',
      photoURL: user?.photoURL || ''
    });
    setProfileMessage(null);
    setIsProfileModalOpen(true);
  };

  const displayName = user?.displayName ? user.displayName.split(' ')[0] : (user?.email?.split('@')[0] || 'Member');

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-t-2 border-gold rounded-full animate-spin mb-4"></div>
          <span className="text-[10px] uppercase tracking-widest text-gold animate-pulse font-bold text-center">Syncing Executive Data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 flex flex-col bg-theme-bg">
      <div className="theme-main-grid">
        {/* Main Content Pane */}
        <div className="content-pane border-r border-theme-border/50">
          <div className="flex justify-between items-start mb-12">
            <div>
              <span className="kicker">Executive Dashboard</span>
              <h1 className="text-4xl md:text-5xl">Welcome back,<br />{displayName}.</h1>
            </div>
            <button 
              onClick={handleLogout}
              className="flex items-center space-x-2 text-taupe hover:text-gold transition-colors text-[10px] uppercase tracking-widest font-bold border border-theme-border p-3 hover:bg-gold/5"
            >
              <LogOut className="h-3 w-3" />
              <span>Terminate Session</span>
            </button>
          </div>

          {/* Diagnostic Synthesis Section (AI) */}
          <section className="mb-20">
            <div className="flex items-center justify-between mb-8 border-b border-theme-border pb-4">
              <h2 className="text-xl font-serif italic text-theme-text flex items-center gap-2">Comprehensive Diagnostic Synthesis <Sparkles className="w-4 h-4 text-gold mb-1" /></h2>
            </div>
            
            <div className="bg-black/30 border border-theme-border p-8 shadow-inner relative group min-h-[300px]">
               {profileData?.diagnosticReport ? (
                 <div>
                    <div className="flex justify-between items-center mb-8 pb-4 border-b border-theme-border/50">
                      <span className="text-[10px] uppercase tracking-widest text-taupe block font-bold">Generated: {profileData.lastReportDate ? (typeof profileData.lastReportDate.toDate === 'function' ? profileData.lastReportDate.toDate().toLocaleDateString() : new Date(profileData.lastReportDate).toLocaleDateString()) : 'Recently'}</span>
                      <button 
                         onClick={generateDiagnosticSynthesis}
                         disabled={isGenerating || audits.length === 0}
                         className="flex items-center space-x-2 text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-all font-bold disabled:opacity-50"
                      >
                         {isGenerating ? 'Compiling Report...' : 'Re-Run Synthesis'}
                      </button>
                    </div>
                    <div className="markdown-body text-sm text-theme-text/80 leading-relaxed font-light mt-4">
                      <Markdown>{profileData.diagnosticReport}</Markdown>
                    </div>
                 </div>
               ) : (
                 <div className="flex flex-col items-center justify-center h-full text-center h-[200px]">
                    <ShieldCheck className="h-10 w-10 text-gold/30 mx-auto mb-6" />
                    <p className="text-taupe mb-8 italic text-sm">
                      {isGenerating ? "Compiling your updated clinical synthesis..." : "No synthesis compiled. You must complete at least one diagnostic audit first."}
                    </p>
                    <button 
                       onClick={generateDiagnosticSynthesis}
                       disabled={isGenerating || audits.length === 0}
                       className="btn-gold px-12 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                       {isGenerating ? (
                         <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-theme-bg border-t-transparent rounded-full animate-spin"></div> Extracting Clinical Data...</span>
                       ) : 'Generate Clinical Synthesis'}
                    </button>
                 </div>
               )}
            </div>
          </section>

          {/* Audit History Log */}
          <section className="mb-20">
            <div className="flex items-center justify-between mb-8 border-b border-theme-border pb-4">
              <h2 className="text-xl font-serif italic text-theme-text">Diagnostic Audit History</h2>
              <Link to="/audit" className="text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-all font-bold">Open Hub</Link>
            </div>
            
            <div className="grid gap-4">
              {audits && audits.length > 0 ? (
                audits.map((a, i) => (
                  <div key={a.id || i} className="bg-black/20 border border-theme-border/50 p-6 flex flex-col md:flex-row justify-between items-start md:items-center group hover:border-gold/30 transition-colors">
                     <div className="mb-4 md:mb-0">
                        <div className="flex items-center gap-3 mb-2">
                           <CheckCircle2 className="h-4 w-4 text-gold" />
                           <h4 className="font-bold text-sm tracking-widest uppercase text-theme-text">{a.title}</h4>
                        </div>
                        <p className="text-[10px] text-taupe uppercase tracking-widest pl-7">Date: {a.timestamp.toLocaleDateString()}</p>
                     </div>
                     <div className="pl-0 md:pl-6 border-l-0 md:border-l border-theme-border/30">
                        <span className="text-gold font-serif italic text-lg leading-tight block">{a.diagnostic}</span>
                     </div>
                  </div>
                ))
              ) : (
                <div className="bg-black/20 border border-theme-border p-12 text-center border-dashed">
                  <Info className="h-8 w-8 text-taupe/40 mx-auto mb-4" />
                  <p className="text-taupe mb-6 italic">No relational infrastructure diagnostics on file.</p>
                  <Link to="/audit" className="btn-gold px-8 inline-block">Commence First Audit</Link>
                </div>
              )}
            </div>
          </section>

          {/* Vetting Tracker Section */}
          <section className="mb-20">
            <div className="flex items-center justify-between mb-8 border-b border-theme-border pb-4">
              <h2 className="text-xl font-serif italic text-theme-text">Vetting Dossiers</h2>
              <button 
                onClick={() => setIsVettingModalOpen(true)}
                className="flex items-center space-x-2 text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-all font-bold"
              >
                <Plus className="h-3 w-3" />
                <span>Open New Dossier</span>
              </button>
            </div>
            
            <div className="space-y-4">
              {vettingProfiles.length > 0 ? (
                vettingProfiles.map((profile) => (
                  <div 
                    key={profile.id} 
                    className="vault-item group px-6 py-6 border border-theme-border/50 hover:border-gold/30 cursor-pointer"
                    onClick={() => navigate(`/dashboard/dossier/${profile.id}`)}
                  >
                    <div className="flex-grow flex items-center space-x-6">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        profile.stage === 'Verified' ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" :
                        profile.stage === 'Restricted' ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" : 
                        "bg-gold animate-pulse shadow-[0_0_8px_rgba(212,175,55,0.5)]"
                      )} />
                      <div>
                        <h4 className="text-sm font-bold tracking-wide group-hover:text-gold transition-colors">{profile.name}</h4>
                        <span className="text-[9px] uppercase tracking-widest text-taupe">{profile.stage} Phase // {profile.riskLevel} Risk</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <button 
                         className="text-[10px] uppercase tracking-widest text-gold/60 hover:text-gold font-bold"
                         onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/dossier/${profile.id}`);
                         }}
                      >Details</button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVetting(profile.id!);
                        }}
                        className="text-taupe hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-taupe italic border border-theme-border/30 rounded-lg">
                  No active vetting processes detected. Vigilance is recommended.
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Sidebar Pane */}
        <aside className="sidebar-pane lg:sticky lg:top-32 h-fit">
          <div className="space-y-12">
            {/* Profile Briefing Card */}
            <div className="border border-gold/20 p-8 bg-gold/5 backdrop-blur-md relative overflow-hidden group hover:bg-gold/10 transition-colors">
              <div className="absolute top-0 left-0 w-1 h-full bg-gold/50"></div>
              <div className="flex items-center space-x-5 mb-8">
                <button 
                  onClick={openProfileEdit}
                  className="relative group/avatar"
                >
                  <div className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center bg-theme-bg overflow-hidden p-1 shadow-xl transition-transform group-hover/avatar:scale-105">
                    {(profileData?.photoURL || user?.photoURL) ? (
                      <img src={profileData?.photoURL || user?.photoURL || ''} alt="Profile" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gold/10 flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-gold" />
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/avatar:opacity-100 rounded-full transition-opacity">
                    <Settings className="h-4 w-4 text-white" />
                  </div>
                </button>
                <div className="flex-grow">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-1">Status: Verified</span>
                  <div className="flex items-center justify-between">
                    <span className="block text-lg font-serif italic text-theme-text leading-tight">{profileData?.displayName || user?.displayName || (user?.email?.split('@')[0])}</span>
                    <button 
                      onClick={openProfileEdit}
                      className="text-taupe/40 hover:text-gold transition-colors"
                    >
                      <Settings className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-4 border-t border-gold/10 pt-6">
                <div className="flex justify-between items-center text-[10px] uppercase tracking-[0.2em] text-taupe font-semibold">
                  <span>Enrolled</span>
                  <span className="text-theme-text">{new Date(user?.metadata.creationTime || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between items-center text-[10px] uppercase tracking-[0.2em] text-taupe font-semibold">
                  <span>Dossiers</span>
                  <span className="text-gold">{vettingProfiles.length} Active</span>
                </div>
              </div>
            </div>

            {/* Invariants Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <span className="kicker">Primary Invariants</span>
                <span className="text-[10px] text-gold font-bold animate-pulse">Live Sync</span>
              </div>
              <div className="space-y-3">
                {invariants.length > 0 ? (
                  invariants.map((inv) => (
                    <button 
                      key={inv.id}
                      onClick={() => toggleInvariant(inv)}
                      className={cn(
                        "w-full text-left p-4 border transition-all duration-300 group flex items-start space-x-4",
                        inv.isActive ? "border-gold/30 bg-gold/5 text-theme-text" : "border-theme-border/50 text-taupe hover:border-gold/20"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 border mt-0.5 flex items-center justify-center shrink-0 transition-colors",
                        inv.isActive ? "border-gold bg-gold text-theme-bg" : "border-taupe group-hover:border-gold"
                      )}>
                        {inv.isActive && <CheckCircle2 className="h-3 w-3" />}
                      </div>
                      <div className="flex-grow">
                        <span className="block text-[9px] uppercase tracking-widest font-bold mb-1 opacity-70">{inv.category}</span>
                        <p className="text-[11px] leading-relaxed italic">{inv.rule}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 border border-theme-border/30 text-center italic text-taupe text-xs">
                    Define your non-negotiable standards to activate invariant tracking.
                  </div>
                )}
              </div>
            </div>

            {/* Vault Spotlight */}
            <div>
               <span className="kicker mb-6">Execution Metrics</span>
               <div className="space-y-6 bg-black/20 p-6 border border-theme-border/50">
                  <div className="flex justify-between items-center text-[10px] uppercase tracking-widest text-taupe">
                    <span className="flex items-center gap-2 italic"><Activity className="h-3 w-3" /> System Health</span>
                    <span className="text-gold font-bold">Stable</span>
                  </div>
                  <div className="h-[1px] bg-theme-border w-full"></div>
                  <p className="text-[11px] leading-relaxed text-taupe italic">
                    "High status is not about what you have, but what you refuse to tolerate."
                  </p>
               </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Vetting Modal */}
      <AnimatePresence>
        {isVettingModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-theme-bg/95 backdrop-blur-md"
              onClick={() => setIsVettingModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-theme-bg border border-gold/30 p-8 md:p-12 shadow-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
              <h2 className="font-serif text-2xl uppercase tracking-widest text-theme-text mb-8">Initiate Vetting Dossier</h2>
              <form onSubmit={handleAddVetting} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-taupe font-bold">Subject Name</label>
                  <input 
                    required
                    type="text" 
                    value={newVetting.name}
                    onChange={e => setNewVetting(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-black/20 border border-theme-border p-4 text-sm focus:border-gold outline-none transition-colors"
                    placeholder="Enter full name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest text-taupe font-bold">Initial Stage</label>
                    <select 
                      value={newVetting.stage}
                      onChange={e => setNewVetting(prev => ({ ...prev, stage: e.target.value as any }))}
                      className="w-full bg-black/20 border border-theme-border p-4 text-sm focus:border-gold outline-none transition-colors appearance-none"
                    >
                      <option value="Observation">Observation</option>
                      <option value="Testing">Testing</option>
                      <option value="Verified">Verified</option>
                      <option value="Restricted">Restricted</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest text-taupe font-bold">Risk Assessment</label>
                    <select 
                      value={newVetting.riskLevel}
                      onChange={e => setNewVetting(prev => ({ ...prev, riskLevel: e.target.value as any }))}
                      className="w-full bg-black/20 border border-theme-border p-4 text-sm focus:border-gold outline-none transition-colors appearance-none"
                    >
                      <option value="Low">Low Risk</option>
                      <option value="Medium">Medium Risk</option>
                      <option value="High">High Risk</option>
                      <option value="Critical">Critical Risk</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-taupe font-bold">Confidential Notes</label>
                  <textarea 
                    value={newVetting.notes}
                    onChange={e => setNewVetting(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full bg-black/20 border border-theme-border p-4 text-sm focus:border-gold outline-none transition-colors min-h-[100px]"
                    placeholder="Initial character observations..."
                  />
                </div>
                <div className="flex space-x-4 pt-4">
                  <button type="submit" className="btn-gold flex-grow">Create Dossier</button>
                  <button 
                    type="button" 
                    onClick={() => setIsVettingModalOpen(false)}
                    className="px-8 bg-transparent border border-theme-border text-taupe uppercase tracking-widest text-[10px] font-bold hover:text-theme-text transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <AnimatePresence>
        {isProfileModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-theme-bg/95 backdrop-blur-md"
              onClick={() => setIsProfileModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-theme-bg border border-gold/30 p-8 md:p-10 shadow-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
              <h2 className="font-serif text-2xl uppercase tracking-widest text-theme-text mb-8">Edit Member Profile</h2>
              
              {profileMessage && (
                <div className={cn(
                  "p-4 mb-8 text-[10px] uppercase tracking-widest border",
                  profileMessage.type === 'success' ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" : "bg-rose-500/10 border-rose-500/30 text-rose-500"
                )}>
                  {profileMessage.text}
                </div>
              )}

              <form onSubmit={handleSaveProfile} className="space-y-8">
                <div className="flex flex-col items-center mb-8">
                  <div className="relative group/upload mb-4">
                    <div className="w-24 h-24 rounded-full border-2 border-gold/30 overflow-hidden p-1 bg-black/20">
                      {profileForm.photoURL ? (
                        <img src={profileForm.photoURL} alt="Preview" className="w-full h-full object-cover rounded-full" />
                      ) : (profileData?.photoURL || user?.photoURL) ? (
                        <img src={profileData?.photoURL || user?.photoURL || ''} alt="Current" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gold/5 flex items-center justify-center">
                          <UserIcon className="h-8 w-8 text-gold/20" />
                        </div>
                      )}
                    </div>
                    <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/upload:opacity-100 rounded-full cursor-pointer transition-opacity">
                      <Plus className="h-6 w-6 text-gold" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleProfileImageChange}
                      />
                    </label>
                  </div>
                  <div className="text-center space-y-1">
                    <span className="text-[9px] uppercase tracking-widest text-taupe block">Click to redact prototype image</span>
                    <span className="text-[8px] uppercase tracking-widest text-gold font-bold block">(Limit: 500KB)</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-taupe font-bold">Public Pseudonym</label>
                  <input 
                    type="text" 
                    value={profileForm.displayName}
                    onChange={e => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full bg-black/20 border border-theme-border p-4 text-sm focus:border-gold outline-none transition-colors"
                    placeholder="Member Name"
                    required
                  />
                  <p className="text-[9px] text-taupe/60 italic tracking-wider">This moniker will be used in all executive correspondence.</p>
                </div>

                <div className="flex space-x-4 pt-4">
                  <button 
                    type="submit" 
                    disabled={savingProfile}
                    className="btn-gold flex-grow disabled:opacity-50 disabled:cursor-not-allowed h-12 flex items-center justify-center"
                  >
                    {savingProfile ? (
                      <div className="w-4 h-4 border-2 border-theme-bg border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setIsProfileModalOpen(false)}
                    className="px-8 bg-transparent border border-theme-border text-taupe uppercase tracking-widest text-[10px] font-bold hover:text-theme-text transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
